import { Client } from '@langchain/langgraph-sdk'
import { createUIMessageStream } from 'ai'

function sourceFromMessage(message) {
  if (!message || typeof message !== 'object') return message

  return message.type === 'constructor' &&
    message.kwargs &&
    typeof message.kwargs === 'object'
    ? message.kwargs
    : message
}

export function textFromContent(content) {
  if (content == null) return ''
  if (typeof content === 'string') return content
  if (typeof content === 'number' || typeof content === 'boolean')
    return String(content)

  if (Array.isArray(content)) {
    return content.map(textFromContent).join('')
  }

  if (typeof content !== 'object') return ''

  if (typeof content.text === 'string') return content.text
  if (typeof content.content === 'string') return content.content
  if (typeof content.delta === 'string') return content.delta
  if (typeof content.value === 'string') return content.value

  if (Array.isArray(content.text)) return textFromContent(content.text)
  if (Array.isArray(content.content)) return textFromContent(content.content)
  if (Array.isArray(content.delta)) return textFromContent(content.delta)

  return ''
}

function contentBlocksFromMessage(message) {
  if (!message || typeof message !== 'object') return []

  const source = sourceFromMessage(message)

  if (Array.isArray(source.contentBlocks)) return source.contentBlocks
  if (Array.isArray(source.content_blocks)) return source.content_blocks
  if (Array.isArray(source.content)) return source.content

  return []
}

// Reasoning and tool arguments are separate parts, never assistant answer text.
function assistantTextFromMessage(message) {
  const source = sourceFromMessage(message)
  if (!Array.isArray(source?.content)) return textFromContent(source?.content)

  return textFromContent(
    source.content.filter(
      (block) =>
        typeof block === 'string' ||
        ['text', 'output_text', 'input_text'].includes(block?.type)
    )
  )
}

function reasoningFromSummary(summary) {
  if (!Array.isArray(summary)) return ''

  return summary
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && typeof item.text === 'string') {
        return item.text
      }

      return ''
    })
    .join('')
}

export function reasoningDisplayFromText(text) {
  const content = typeof text === 'string' ? text : textFromContent(text)

  if (!content.startsWith('**')) {
    return { title: '', text: content }
  }

  const titleStart = 2
  const closingMarkerIndex = content.indexOf('**', titleStart)

  if (closingMarkerIndex === -1) {
    const lineBreakIndex = content.search(/\r?\n/)

    if (lineBreakIndex !== -1) {
      return { title: '', text: content }
    }

    return {
      title: content.slice(titleStart).trim(),
      text: '',
    }
  }

  const title = content.slice(titleStart, closingMarkerIndex).trim()
  const afterTitle = content.slice(closingMarkerIndex + 2)

  if (!title || (afterTitle && !/^[ \t]*(\r?\n|$)/.test(afterTitle))) {
    return { title: '', text: content }
  }

  return {
    title,
    text: afterTitle.replace(/^\s+/, ''),
  }
}

export function reasoningFromMessage(message) {
  if (!message || typeof message !== 'object') return ''

  const source = sourceFromMessage(message)

  const fromBlocks = contentBlocksFromMessage(source)
    .map((block) => {
      if (!block || typeof block !== 'object') return ''
      if (block.type === 'reasoning' && typeof block.reasoning === 'string') {
        return block.reasoning
      }
      if (block.type === 'reasoning' && Array.isArray(block.summary)) {
        return reasoningFromSummary(block.summary)
      }
      if (block.type === 'reasoning' && typeof block.text === 'string') {
        return block.text
      }
      if (block.type === 'thinking' && typeof block.thinking === 'string') {
        return block.thinking
      }

      return ''
    })
    .join('')

  if (fromBlocks) return fromBlocks

  const additionalReasoning = source.additional_kwargs?.reasoning
  if (typeof additionalReasoning === 'string') return additionalReasoning
  const routerReasoning = source.additional_kwargs?.reasoning_details
    ?.map((detail) => detail.text || detail.summary || '')
    .join('')
  if (routerReasoning) return routerReasoning
  const additionalSummary = reasoningFromSummary(additionalReasoning?.summary)

  if (additionalSummary) return additionalSummary

  const output = source.response_metadata?.output

  if (Array.isArray(output)) {
    return output
      .filter((item) => item?.type === 'reasoning')
      .map((item) => reasoningFromSummary(item.summary))
      .join('')
  }

  return ''
}

function parseJsonValue(value) {
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function toolTypeForName(name) {
  return `tool-${name || 'tool'}`
}

export function toolNameFromPart(part) {
  if (part?.type === 'dynamic-tool') return part.toolName
  if (typeof part?.type === 'string' && part.type.startsWith('tool-')) {
    return part.type.slice('tool-'.length)
  }

  return 'tool'
}

export function isToolPart(part) {
  return (
    part?.type === 'dynamic-tool' ||
    (typeof part?.type === 'string' && part.type.startsWith('tool-'))
  )
}

const toolStateRank = {
  'input-streaming': 1,
  'approval-requested': 2,
  'approval-responded': 3,
  'input-available': 4,
  'output-denied': 5,
  'output-error': 5,
  'output-available': 5,
}

function rankedToolState(part) {
  return toolStateRank[part?.state] || 0
}

function mergeToolPart(previous, next) {
  const dominant =
    rankedToolState(next) >= rankedToolState(previous) ? next : previous
  const merged = {
    ...previous,
    ...next,
    state: dominant.state,
  }

  if (next.input === undefined && previous.input !== undefined) {
    merged.input = previous.input
  }

  if (dominant.output !== undefined) {
    merged.output = dominant.output
  } else {
    delete merged.output
  }

  if (dominant.errorText !== undefined) {
    merged.errorText = dominant.errorText
  } else {
    delete merged.errorText
  }

  return merged
}

export function mergeDuplicateToolParts(parts = []) {
  const merged = []
  const toolPartIndexes = new Map()

  for (const part of parts) {
    if (!isToolPart(part) || !part.toolCallId) {
      merged.push(part)
      continue
    }

    const existingIndex = toolPartIndexes.get(part.toolCallId)

    if (existingIndex === undefined) {
      toolPartIndexes.set(part.toolCallId, merged.length)
      merged.push({ ...part })
      continue
    }

    merged[existingIndex] = mergeToolPart(merged[existingIndex], part)
  }

  return merged
}

function toolInputFromCall(call) {
  if (!call || typeof call !== 'object') return undefined

  if (call.arguments !== undefined) return parseJsonValue(call.arguments)
  if (call.args !== undefined) return call.args
  if (call.input !== undefined) return call.input

  return undefined
}

function toolInputTextFromCall(call) {
  if (!call || typeof call !== 'object') return ''

  if (typeof call.arguments === 'string') return call.arguments
  if (typeof call.args === 'string') return call.args
  if (typeof call.input === 'string') return call.input

  return ''
}

function toolInputValueFromCall(call) {
  if (!call || typeof call !== 'object') return undefined

  if (call.arguments !== undefined && typeof call.arguments !== 'string') {
    return call.arguments
  }
  if (call.args !== undefined && typeof call.args !== 'string') {
    return call.args
  }
  if (call.input !== undefined && typeof call.input !== 'string') {
    return call.input
  }

  return undefined
}

function toolCallIdFromCall(call) {
  return call?.call_id || call?.callId || call?.id
}

function parseCompleteToolInput(text) {
  if (typeof text !== 'string') return undefined

  const trimmed = text.trim()

  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
    return undefined
  }

  const parsed = parseJsonValue(trimmed)

  if (parsed && typeof parsed === 'object') return parsed

  return undefined
}

function mergeToolInputText(previous, next) {
  if (!next) return previous || ''
  if (!previous) return next
  if (next.startsWith(previous)) return next

  return `${previous}${next}`
}

function toolCallsFromMessage(message) {
  const source = sourceFromMessage(message)
  if (!source || typeof source !== 'object') return []

  const parts = []
  const seenCallIds = new Set()

  for (const block of contentBlocksFromMessage(source)) {
    if (!block || typeof block !== 'object' || block.type !== 'function_call') {
      continue
    }

    const toolCallId =
      block.call_id || block.callId || block.id || crypto.randomUUID()
    seenCallIds.add(toolCallId)
    parts.push({
      type: toolTypeForName(block.name),
      toolCallId,
      state: 'input-available',
      input: toolInputFromCall(block),
    })
  }

  if (Array.isArray(source.tool_calls)) {
    for (const call of source.tool_calls) {
      if (!call || typeof call !== 'object') continue

      const toolCallId =
        call.id || call.call_id || call.callId || crypto.randomUUID()
      if (seenCallIds.has(toolCallId)) continue

      seenCallIds.add(toolCallId)
      parts.push({
        type: toolTypeForName(call.name),
        toolCallId,
        state: 'input-available',
        input: toolInputFromCall(call),
      })
    }
  }

  return parts
}

function toolCallChunksFromMessage(message) {
  const source = sourceFromMessage(message)
  if (!source || typeof source !== 'object') return []
  if (
    Array.isArray(source.tool_call_chunks) &&
    source.tool_call_chunks.length
  ) {
    return source.tool_call_chunks
  }

  const chunks = []

  for (const block of contentBlocksFromMessage(source)) {
    if (
      block &&
      typeof block === 'object' &&
      ['function_call', 'tool_call', 'tool_call_chunk'].includes(block.type)
    ) {
      chunks.push(block)
    }
  }

  if (Array.isArray(source.tool_calls)) {
    chunks.push(
      ...source.tool_calls.filter((call) => call && typeof call === 'object')
    )
  }

  return chunks
}

function updatePendingToolInputs(state, message) {
  const source = sourceFromMessage(message)
  for (const call of toolCallChunksFromMessage(message)) {
    const indexKey = `${source?.id || 'message'}:${call.index}`
    const toolCallId =
      toolCallIdFromCall(call) || state.toolChunkIndexes.get(indexKey)

    if (!toolCallId) continue
    if (call.index != null) state.toolChunkIndexes.set(indexKey, toolCallId)

    const pending = state.pendingToolInputs.get(toolCallId) || {
      inputText: '',
      input: undefined,
      name: undefined,
    }

    if (call.name) pending.name = call.name

    const inputValue = toolInputValueFromCall(call)
    if (inputValue !== undefined) pending.input = inputValue

    const inputText = toolInputTextFromCall(call)
    if (inputText) {
      pending.inputText = mergeToolInputText(pending.inputText, inputText)
      const parsedInput = parseCompleteToolInput(pending.inputText)

      if (parsedInput !== undefined) {
        pending.input = parsedInput
      }
    }

    state.pendingToolInputs.set(toolCallId, pending)
  }
}

function flushPendingToolInput(writer, state, toolCallId, fallbackName) {
  if (state.toolInputIds.has(toolCallId)) return true

  const pending = state.pendingToolInputs.get(toolCallId)
  const toolName = pending?.name || fallbackName

  if (!toolName || pending?.input === undefined) return false

  state.toolInputIds.add(toolCallId)
  writer.write({
    type: 'tool-input-available',
    toolCallId,
    toolName,
    input: pending.input,
  })

  return true
}

function flushCompletePendingToolInputs(writer, state) {
  for (const [toolCallId, pending] of state.pendingToolInputs) {
    if (state.toolInputIds.has(toolCallId)) continue
    if (!pending.name || pending.input === undefined) continue

    flushPendingToolInput(writer, state, toolCallId)
  }
}

function toolResultFromMessage(message) {
  const source = sourceFromMessage(message)

  if (!source || typeof source !== 'object' || source.type !== 'tool') {
    return null
  }

  const text = textFromContent(source.content)
  const isError = source.status === 'error'
  const structuredOutput =
    source.artifact?.structured_content ?? source.artifact?.structuredContent

  return {
    type: toolTypeForName(source.name),
    toolCallId:
      source.tool_call_id ||
      source.toolCallId ||
      source.call_id ||
      source.id ||
      crypto.randomUUID(),
    state: isError ? 'output-error' : 'output-available',
    ...(isError
      ? { errorText: text || 'The tool returned an error.' }
      : { output: structuredOutput ?? parseJsonValue(text) }),
  }
}

function mediaTypeFromDataUrl(value) {
  if (typeof value !== 'string') return null

  const match = value.match(/^data:([^;,]+)[;,]/)

  return match?.[1] || null
}

function mediaTypeFromFilename(filename) {
  if (typeof filename !== 'string') return null

  const extension = filename.split('.').pop()?.toLowerCase()

  return (
    {
      csv: 'text/csv',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      gif: 'image/gif',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      json: 'application/json',
      mp3: 'audio/mpeg',
      mp4: 'video/mp4',
      pdf: 'application/pdf',
      png: 'image/png',
      txt: 'text/plain',
      webp: 'image/webp',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }[extension] || null
  )
}

function filePartFromInputFileBlock(block) {
  if (
    !block ||
    typeof block !== 'object' ||
    !['input_file', 'file', 'image'].includes(block.type)
  ) {
    return null
  }

  const url =
    block.file_data ||
    block.fileData ||
    block.url ||
    (block.base64 ? `data:${block.mime_type};base64,${block.base64}` : '')
  const filename = block.filename || block.name || 'attachment'

  return {
    type: 'file',
    filename,
    mediaType:
      block.media_type ||
      block.mediaType ||
      block.mime_type ||
      mediaTypeFromDataUrl(url) ||
      mediaTypeFromFilename(filename) ||
      'application/octet-stream',
    url,
  }
}

function filePartsFromMessage(message) {
  return contentBlocksFromMessage(message)
    .map(filePartFromInputFileBlock)
    .filter(Boolean)
}

export function textFromMessage(message) {
  if (typeof message === 'string') return message

  if (message?.parts) {
    return message.parts
      .filter((part) => part.type === 'text')
      .map((part) => textFromContent(part.text))
      .join('')
  }

  return textFromContent(message?.content)
}

export function partsFromLangGraphMessage(message) {
  message = sourceFromMessage(message)
  const toolResult = toolResultFromMessage(message)
  if (toolResult) return [toolResult]

  const parts = []
  const reasoning = reasoningFromMessage(message)
  const toolParts = toolCallsFromMessage(message)
  const fileParts = filePartsFromMessage(message)
  const text = assistantTextFromMessage(message)

  parts.push(...fileParts)

  if (reasoning.trim()) {
    const displayReasoning = reasoningDisplayFromText(reasoning)
    const reasoningPart = {
      type: 'reasoning',
      text: displayReasoning.text,
      state: 'done',
    }

    if (displayReasoning.title) {
      reasoningPart.title = displayReasoning.title
    }

    parts.push(reasoningPart)
  }

  if (text) {
    parts.push({ type: 'text', text })
  }

  parts.push(...toolParts)

  return parts.length ? parts : [{ type: 'text', text: '' }]
}

function roleForLangGraph(role) {
  if (role === 'assistant') return 'assistant'
  if (role === 'system') return 'system'

  return 'user'
}

function isDataUrl(value) {
  return typeof value === 'string' && /^data:[^;]+;base64,/.test(value)
}

function inputFileBlockFromPart(part) {
  if (!isDataUrl(part?.url)) return null

  const suppliedMime = mediaTypeFromDataUrl(part.url) || part.mediaType
  const mime =
    !suppliedMime || suppliedMime === 'application/octet-stream'
      ? mediaTypeFromFilename(part.filename) || suppliedMime
      : suppliedMime

  return {
    type: mime?.startsWith('image/') ? 'image' : 'file',
    filename: part.filename || 'attachment',
    mime_type: mime,
    base64: part.url.slice(part.url.indexOf(',') + 1),
  }
}

function inputTextBlockFromPart(part) {
  const text = textFromContent(part?.text)

  if (!text) return null

  return {
    type: 'text',
    text,
  }
}

export function contentBlocksForLangGraphMessage(message) {
  if (!Array.isArray(message?.parts)) {
    const text = textFromContent(message?.content)

    return text ? [{ type: 'text', text }] : []
  }

  return message.parts
    .map((part) => {
      if (part.type === 'text') return inputTextBlockFromPart(part)
      if (part.type === 'file') return inputFileBlockFromPart(part)

      return null
    })
    .filter(Boolean)
}

export function toLangGraphMessage(message) {
  const contentBlocks = contentBlocksForLangGraphMessage(message)

  return {
    ...(message.id ? { id: message.id } : {}),
    role: roleForLangGraph(message.role),
    content: contentBlocks.length ? contentBlocks : textFromMessage(message),
  }
}

export function messagesFromLangGraphStateMessages(langGraphMessages = []) {
  const messages = []
  const toolPartsByCallId = new Map()

  for (const serializedMessage of langGraphMessages) {
    const message = sourceFromMessage(serializedMessage)
    const toolResult = toolResultFromMessage(message)

    if (toolResult) {
      const existingPart = toolPartsByCallId.get(toolResult.toolCallId)

      if (existingPart) {
        Object.assign(existingPart, {
          ...toolResult,
          input: existingPart.input,
        })
      } else {
        messages.push({
          id: message.id || crypto.randomUUID(),
          role: 'assistant',
          parts: [toolResult],
        })
      }

      continue
    }

    const parts = partsFromLangGraphMessage(message)

    for (const part of parts) {
      if (part.toolCallId) {
        toolPartsByCallId.set(part.toolCallId, part)
      }
    }

    messages.push({
      id: message.id || crypto.randomUUID(),
      role:
        message.type === 'human'
          ? 'user'
          : message.type === 'ai'
            ? 'assistant'
            : message.role,
      parts,
      ...(message.additional_kwargs?.bidboost_activity
        ? {
            metadata: {
              agent: {
                sourceMessageId: message.id,
                ...message.additional_kwargs.bidboost_activity,
              },
            },
          }
        : {}),
    })
  }

  return messages
}

function findInterrupt(chunk) {
  const data = chunk?.data

  if (!data) return null

  if (data.__interrupt__) return data.__interrupt__
  if (data.interrupts) return data.interrupts

  if (Array.isArray(data)) {
    return data.find((item) => item?.value?.action_requests)
  }

  for (const value of Object.values(data)) {
    if (value?.__interrupt__) return value.__interrupt__
    if (value?.interrupts) return value.interrupts
  }

  return null
}

function interruptValue(interrupt) {
  return Array.isArray(interrupt)
    ? interrupt[0]?.value || interrupt[0]
    : interrupt?.value || interrupt
}

export function findInterruptInState(state) {
  const valueInterrupts = state?.values?.__interrupt__

  if (Array.isArray(valueInterrupts) && valueInterrupts.length) {
    return interruptValue(valueInterrupts)
  }

  if (
    state?.interrupts &&
    (!Array.isArray(state.interrupts) || state.interrupts.length)
  ) {
    return interruptValue(state.interrupts)
  }

  const taskInterrupts = (state?.tasks || [])
    .flatMap((task) => task?.interrupts || [])
    .filter(Boolean)

  if (taskInterrupts.length) {
    return interruptValue(taskInterrupts)
  }

  return null
}

function writeMessageChunk(writer, state, message) {
  const reasoning = reasoningFromMessage(message)
  const toolResult = toolResultFromMessage(message)
  const text = toolResult ? '' : assistantTextFromMessage(message)

  updatePendingToolInputs(state, message)

  if (reasoning) {
    if (!state.reasoningStarted) {
      state.reasoningStarted = true
      writer.write({ type: 'reasoning-start', id: state.reasoningId })
    }

    writer.write({
      type: 'reasoning-delta',
      id: state.reasoningId,
      delta: reasoning,
    })
  }

  flushCompletePendingToolInputs(writer, state)

  if (toolResult) {
    if (state.reasoningStarted) {
      writer.write({ type: 'reasoning-end', id: state.reasoningId })
      state.reasoningStarted = false
    }

    if (state.textStarted) {
      writer.write({ type: 'text-end', id: state.textId })
      state.textStarted = false
    }
  }

  if (toolResult && !state.toolOutputIds.has(toolResult.toolCallId)) {
    if (
      !flushPendingToolInput(
        writer,
        state,
        toolResult.toolCallId,
        toolNameFromPart(toolResult)
      )
    ) {
      state.toolInputIds.add(toolResult.toolCallId)
      writer.write({
        type: 'tool-input-available',
        toolCallId: toolResult.toolCallId,
        toolName: toolNameFromPart(toolResult),
        input: undefined,
      })
    }

    state.toolOutputIds.add(toolResult.toolCallId)

    if (toolResult.state === 'output-error') {
      writer.write({
        type: 'tool-output-error',
        toolCallId: toolResult.toolCallId,
        errorText: toolResult.errorText,
      })
    } else {
      writer.write({
        type: 'tool-output-available',
        toolCallId: toolResult.toolCallId,
        output: toolResult.output,
      })
    }
  }

  if (text) {
    if (state.reasoningStarted) {
      writer.write({ type: 'reasoning-end', id: state.reasoningId })
      state.reasoningStarted = false
    }

    if (!state.textStarted) {
      state.textStarted = true
      writer.write({ type: 'text-start', id: state.textId })
    }

    writer.write({
      type: 'text-delta',
      id: state.textId,
      delta: text,
    })
  }
}

function finishMessageChunk(writer, state) {
  if (state.reasoningStarted) {
    writer.write({ type: 'reasoning-end', id: state.reasoningId })
    state.reasoningStarted = false
  }

  if (state.textStarted) {
    writer.write({ type: 'text-end', id: state.textId })
    state.textStarted = false
  }

  for (const source of state.sources.values())
    finishSourceReasoning(writer, source)
}

function createStreamState() {
  return {
    reasoningId: crypto.randomUUID(),
    reasoningStarted: false,
    textId: crypto.randomUUID(),
    textStarted: false,
    toolInputIds: new Set(),
    toolOutputIds: new Set(),
    pendingToolInputs: new Map(),
    toolChunkIndexes: new Map(),
    sources: new Map(),
    sourceAliases: new Map(),
    sourceInvocations: new Map(),
    ledger: new Map(),
  }
}

function publishSourceMessage(writer, source) {
  writer.write({
    type: 'data-agent-message',
    id: source.id,
    data: {
      id: source.id,
      role: 'assistant',
      parts: source.parts,
      metadata: source.metadata,
    },
  })
}

function finishSourceReasoning(writer, source) {
  if (
    !source.parts?.some(
      (part) => part.type === 'reasoning' && part.state === 'streaming'
    )
  )
    return
  source.reasoningComplete = true
  source.parts = source.parts.map((part) =>
    part.type === 'reasoning' ? { ...part, state: 'done' } : part
  )
  publishSourceMessage(writer, source)
}

function generatedMessageId(id) {
  return /^(lc_run-|lc_|run--)/.test(id)
}

function sourceInvocation(namespace, metadata) {
  if (!metadata.langgraph_checkpoint_ns && metadata.langgraph_step == null)
    return null
  return JSON.stringify([
    namespace,
    metadata.langgraph_checkpoint_ns,
    metadata.langgraph_node,
    metadata.langgraph_step,
  ])
}

function hiddenModelMessage(metadata = {}) {
  return (
    (metadata.tags || []).some((tag) =>
      ['langsmith:nostream', 'nostream', 'langsmith:hidden'].includes(tag)
    ) || /AutoReviewMiddleware/.test(metadata.langgraph_node || '')
  )
}

// The AI SDK combines a run into one assistant message. Keep the original
// LangGraph messages alongside its tool parts so the UI can distinguish a
// progress update, a subagent answer, and the root agent's final answer.
function writeSourceMessage(
  writer,
  state,
  message,
  metadata = {},
  event = '',
  snapshot = false
) {
  const source = sourceFromMessage(message)
  if (!source || hiddenModelMessage(metadata)) return
  if (['human', 'system', 'remove'].includes(source.type)) return

  const identity = source.additional_kwargs?.bidboost_activity || {}
  const namespace =
    identity.source === 'root' ? '' : event.split('|').slice(1).join('|')
  const sourceId =
    source.id ||
    `${namespace || 'root'}:${source.tool_call_id || metadata.langgraph_step || 'message'}`
  const invocation =
    source.type !== 'tool' && sourceInvocation(namespace, metadata)
  const invocationSource = state.sources.get(
    state.sourceInvocations.get(invocation)
  )
  // Responses API emits its provider ID on the first (empty) chunk, then
  // LangChain uses an lc_run ID for the remaining deltas. They belong to the
  // same graph invocation; content equality cannot establish that identity.
  const alias =
    invocationSource &&
    !invocationSource.metadata.agent.sourceMessageIds.some(
      (id) => generatedMessageId(id) === generatedMessageId(sourceId)
    )
      ? invocationSource.id
      : null
  const id = state.sourceAliases.get(sourceId) || alias || sourceId
  const previous = state.sources.get(id)

  if (!previous) {
    for (const previousSource of state.sources.values()) {
      if (previousSource.metadata.agent.namespace === namespace)
        finishSourceReasoning(writer, previousSource)
    }
  }
  const current =
    snapshot || !previous
      ? {
          id,
          role: 'assistant',
          text: '',
          reasoning: '',
          reasoningComplete: false,
          tools: new Map(),
          toolIndexes: new Map(),
          metadata: {
            agent: {
              sourceMessageId: id,
              sourceMessageIds: [],
              namespace,
              source: identity.source || (namespace ? 'subagent' : 'root'),
              kind: source.type === 'tool' ? 'tool' : 'assistant',
              parentToolCallId:
                identity.parent_tool_call_id ||
                metadata.parent_tool_call_id ||
                null,
              turnId: identity.turn_id,
            },
          },
        }
      : previous

  if (!current.metadata.agent.sourceMessageIds.includes(sourceId))
    current.metadata.agent.sourceMessageIds.push(sourceId)
  state.sourceAliases.set(sourceId, id)
  if (invocation) state.sourceInvocations.set(invocation, id)

  current.text += source.type === 'tool' ? '' : assistantTextFromMessage(source)
  current.reasoning += reasoningFromMessage(source)
  current.metadata.agent = {
    ...current.metadata.agent,
    ...(identity.source ? { source: identity.source } : {}),
    ...(identity.parent_tool_call_id
      ? { parentToolCallId: identity.parent_tool_call_id }
      : {}),
    ...(identity.turn_id ? { turnId: identity.turn_id } : {}),
  }
  const toolResult = toolResultFromMessage(source)
  if (toolResult) current.tools.set(toolResult.toolCallId, toolResult)

  // A provider may put the complete tool call and its chunk in the same event.
  const calls =
    Array.isArray(source.tool_call_chunks) && source.tool_call_chunks.length
      ? source.tool_call_chunks
      : toolCallChunksFromMessage(source)
  for (const call of calls) {
    const callId =
      toolCallIdFromCall(call) || current.toolIndexes.get(call.index)
    if (!callId) continue
    if (call.index != null) current.toolIndexes.set(call.index, callId)
    const previousCall = current.tools.get(callId) || {
      type: 'tool-tool',
      toolCallId: callId,
    }
    const inputText = mergeToolInputText(
      previousCall.inputText,
      toolInputTextFromCall(call)
    )
    const input =
      toolInputValueFromCall(call) ??
      parseCompleteToolInput(inputText) ??
      previousCall.input
    current.tools.set(callId, {
      ...previousCall,
      type: call.name ? toolTypeForName(call.name) : previousCall.type,
      state: input === undefined ? 'input-streaming' : 'input-available',
      input,
      inputText,
    })
  }

  const parts = []
  if (
    snapshot ||
    current.text ||
    current.tools.size ||
    source.chunk_position === 'last' ||
    source.response_metadata?.finish_reason ||
    source.response_metadata?.stop_reason
  )
    current.reasoningComplete = true
  if (current.reasoning) {
    parts.push({
      type: 'reasoning',
      ...reasoningDisplayFromText(current.reasoning),
      state: current.reasoningComplete ? 'done' : 'streaming',
    })
  }
  if (current.text) parts.push({ type: 'text', text: current.text })
  parts.push(...current.tools.values())
  current.parts = parts
  state.sources.set(id, current)
  publishSourceMessage(writer, current)
}

function writeActivityUpdates(writer, state, value) {
  if (!value || typeof value !== 'object') return
  if (value.bidboost_activity && typeof value.bidboost_activity === 'object') {
    for (const [id, record] of Object.entries(value.bidboost_activity)) {
      if (!record || typeof record !== 'object') continue
      state.ledger.set(id, record)
      writer.write({
        type: 'data-agent-ledger',
        id,
        data: { ...record, id: record.id || id },
      })
    }
  }
  // Updates are keyed by node name; do not descend into message content or
  // tool output, which are application data rather than graph state.
  for (const [key, child] of Object.entries(value)) {
    if (['bidboost_activity', 'messages'].includes(key)) continue
    if (child && typeof child === 'object' && !Array.isArray(child)) {
      if (child.bidboost_activity) writeActivityUpdates(writer, state, child)
    }
  }
}

function historyActivity(state, records = {}) {
  Object.assign(records, state?.values?.bidboost_activity || {})
  const interrupts = [
    ...(Array.isArray(state?.values?.__interrupt__)
      ? state.values.__interrupt__
      : []),
    ...(Array.isArray(state?.interrupts) ? state.interrupts : []),
    ...(state?.tasks || []).flatMap((task) => task.interrupts || []),
  ]
  for (const interrupt of interrupts) {
    Object.assign(records, interruptValue(interrupt)?.bidboost_activity || {})
  }
  for (const task of state?.tasks || []) {
    if (task.state && typeof task.state === 'object')
      historyActivity(task.state, records)
  }
  return records
}

function lastTurnSnapshot(messages = [], turnId) {
  return messages
    .flatMap((message) => message.parts || [])
    .filter(
      (part) => part.type === 'data-agent-turn' && part.data?.id === turnId
    )
    .at(-1)?.data
}

export class BidBoostLangGraphTransport {
  constructor({ sessionUrl, onInterrupt, onModelChange, onModels }) {
    this.sessionUrl = sessionUrl
    this.onInterrupt = onInterrupt
    this.onModelChange = onModelChange
    this.onModels = onModels
    this.modelId = 'sol'
    this.session = null
    this.client = null
    this.threadId = null
  }

  async ensureSession(force = false) {
    if (
      !force &&
      this.session?.token &&
      new Date(this.session.expiresAt).getTime() > Date.now() + 60_000
    ) {
      return this.session
    }

    const { data } = await axios.post(this.sessionUrl)
    this.session = data
    this.onModels?.(data.models || [])
    this.client = new Client({
      apiUrl: data.deploymentUrl,
      defaultHeaders: {
        Authorization: `Bearer ${data.token}`,
      },
    })

    return this.session
  }

  setModel(modelId) {
    if (!this.session?.models?.some((model) => model.id === modelId)) {
      throw new Error('Choose a model from the available options.')
    }
    this.modelId = modelId
    this.onModelChange?.(modelId)
  }

  async listThreads() {
    await this.ensureSession()

    return await this.client.threads.search({
      limit: 25,
      offset: 0,
      sortBy: 'updated_at',
      sortOrder: 'desc',
      select: ['thread_id', 'created_at', 'updated_at', 'metadata', 'status'],
    })
  }

  async reconnectToStream() {
    if (!this.threadId) return null

    await this.ensureSession()

    return createUIMessageStream({
      execute: async ({ writer }) => {
        writer.write({ type: 'start', messageId: crypto.randomUUID() })
        const state = createStreamState()

        for await (const chunk of this.client.threads.joinStream(
          this.threadId,
          {
            streamMode: ['messages-tuple', 'updates'],
          }
        )) {
          const interrupt = findInterrupt(chunk)

          if (interrupt) {
            writeActivityUpdates(writer, state, interruptValue(interrupt))
            this.onInterrupt?.(interruptValue(interrupt))
            continue
          }

          if (String(chunk.event).startsWith('updates')) {
            writeActivityUpdates(writer, state, chunk.data)
          }
          if (chunk.event === 'error') {
            finishMessageChunk(writer, state)
            throw new Error(chunk.data?.message || 'The agent request failed.')
          }

          if (
            !String(chunk.event).startsWith('messages') ||
            !Array.isArray(chunk.data)
          ) {
            continue
          }

          if (hiddenModelMessage(chunk.data[1])) continue
          writeMessageChunk(writer, state, chunk.data[0])
          writeSourceMessage(
            writer,
            state,
            chunk.data[0],
            chunk.data[1],
            String(chunk.event)
          )
        }

        finishMessageChunk(writer, state)
      },
      onError: (error) => error?.message || 'The agent request failed.',
    })
  }

  async selectThread(threadId, metadata = {}) {
    await this.ensureSession()
    this.threadId = threadId
    const state = await this.client.threads.getState(threadId, undefined, {
      subgraphs: true,
    })
    this.modelId =
      state.values?.agent_model ||
      metadata.agent_model ||
      this.session.defaultModel ||
      'sol'
    this.onModelChange?.(this.modelId)
    const interrupt = findInterruptInState(state)

    if (interrupt) {
      this.onInterrupt?.(interrupt)
    }

    const messages = messagesFromLangGraphStateMessages(
      state.values?.messages || []
    )
    const records = Object.values(historyActivity(state))
    if (records.length) {
      messages.push({
        id: `activity:${threadId}`,
        role: 'assistant',
        parts: records.map((record) => ({
          type: 'data-agent-ledger',
          id: record.id,
          data: record,
        })),
      })
    }
    return messages
  }

  async createThread(firstMessage) {
    await this.ensureSession()

    const thread = await this.client.threads.create({
      metadata: {
        title: firstMessage.slice(0, 80),
        agent_model: this.modelId,
      },
    })

    this.threadId = thread.thread_id

    return thread
  }

  async sendMessages(options) {
    const messages = options.messages || []
    const lastMessage = messages[messages.length - 1]

    if (!lastMessage) {
      return createUIMessageStream({ execute() {} })
    }

    await this.ensureSession()

    let seedOrganizationSkills = false

    if (!this.threadId) {
      await this.createThread(textFromMessage(lastMessage))
      seedOrganizationSkills = true
    }

    return this.streamRun({
      input: {
        messages: [toLangGraphMessage(lastMessage)],
        agent_turn_id: lastMessage.id,
        ...(seedOrganizationSkills ? { seed_organization_skills: true } : {}),
      },
      originalMessages: messages,
      signal: options.abortSignal || options.signal,
    })
  }

  async resume(decisions, originalMessages = [], { signal } = {}) {
    await this.ensureSession()

    return this.streamRun({
      command: {
        resume: {
          decisions,
        },
      },
      originalMessages,
      signal,
    })
  }

  streamRun({ input, command, originalMessages, signal }) {
    const modelId = this.modelId
    const turnId =
      input?.agent_turn_id ||
      originalMessages?.findLast((message) => message.role === 'user')?.id ||
      crypto.randomUUID()
    const previousTurn = lastTurnSnapshot(originalMessages, turnId)
    return createUIMessageStream({
      originalMessages,
      execute: async ({ writer }) => {
        writer.write({ type: 'start', messageId: crypto.randomUUID() })
        const state = createStreamState()
        const segmentStartedAt = Date.now()
        const writeTurn = (status) =>
          writer.write({
            type: 'data-agent-turn',
            id: turnId,
            data: {
              id: turnId,
              status,
              durationMs:
                (previousTurn?.durationMs || 0) +
                (status === 'running' ? 0 : Date.now() - segmentStartedAt),
              segmentStartedAt: status === 'running' ? segmentStartedAt : null,
            },
          })
        writeTurn('running')
        let interrupted = false

        const stream = this.client.runs.stream(
          this.threadId,
          this.session.assistantId,
          {
            input,
            command,
            context: { model: modelId },
            streamMode: ['messages-tuple', 'updates'],
            streamSubgraphs: true,
            // Stop must also cancel the server run so follow-up messages can start.
            onDisconnect: 'cancel',
            signal,
          }
        )

        try {
          for await (const chunk of stream) {
            const interrupt = findInterrupt(chunk)

            if (interrupt) {
              interrupted = true
              writeActivityUpdates(writer, state, interruptValue(interrupt))
              this.onInterrupt?.(interruptValue(interrupt))
              writer.write({
                type: 'data-agent-interrupt',
                data: interruptValue(interrupt),
              })
              continue
            }

            if (String(chunk.event).startsWith('updates')) {
              writeActivityUpdates(writer, state, chunk.data)
            }
            if (chunk.event === 'error') {
              throw new Error(
                chunk.data?.message || 'The agent request failed.'
              )
            }

            if (
              !String(chunk.event).startsWith('messages') ||
              !Array.isArray(chunk.data)
            ) {
              continue
            }

            if (hiddenModelMessage(chunk.data[1])) continue
            writeMessageChunk(writer, state, chunk.data[0])
            writeSourceMessage(
              writer,
              state,
              chunk.data[0],
              chunk.data[1],
              String(chunk.event)
            )
          }

          finishMessageChunk(writer, state)
          writeTurn(
            interrupted ? 'waiting' : signal?.aborted ? 'stopped' : 'complete'
          )
        } catch (error) {
          finishMessageChunk(writer, state)
          writeTurn(
            signal?.aborted || error?.name === 'AbortError'
              ? 'stopped'
              : 'error'
          )
          if (!signal?.aborted && error?.name !== 'AbortError') throw error
        }
      },
      onError: (error) => error?.message || 'The agent request failed.',
    })
  }
}
